import {
  booleanLiteralPattern,
  dqlUnitSuffixPattern,
  parameterNamePattern,
  sortedDqlCommands,
} from '../../core/dql/dql-common.js';

const comments = {
  comment: {
    // All characters except for newline, carriage return, form feed, vertical tab characters
    pattern: /\/\/[^\n\r\v\f]*/,
    greedy: true,
  },
  commentMultiline: {
    pattern: /\/\*[\s\S]*?\*\//m,
    alias: 'comment',
    greedy: true,
  },
};

/**
 * Matches double-quoted strings allowing escaped quotes.
 *
 * This regular expression matches strings enclosed in double quotes,
 * where the quotes can be escaped using a backslash.
 * Note that if a backslash is itself escaped (`\\`), it *doesn't* escape the quote that follows.
 *
 * @example
 * // ✔ Matches:
 * // "string"
 * // "string\"with\"quotes"
 * // "string\\"
 * // "string\\\"with\\\"quotes"
 * // "string\\\\"
 *
 * // ✘ Does not match:
 * // "string (missing closing quote)
 * // "string"with"quotes" (only matches "string")
 * // "string\" (missing closing quote)
 * // "string\\"with\\"quotes" (only matches "string\\")
 * // "string\\\" (missing closing quote)
 * // etc.
 *
 * Please be careful when modifying this RegEx. Minor changes may cause exponential runtime complexity:
 * const badPattern = /"(?:[^"\\]+|\\.)*"/m
 *
 * @see Friedl, J. E. (2006). Mastering regular expressions. O'Reilly Media, Inc., pages 196-198, 223-228
 */
const stringPattern = /"(?:[^"\\]|\\.)*"/m;

/**
 * @see stringPattern
 */
const backtickStringPattern = /`(?:[^`\\]|\\.)*`/m;

/**
 * Matches everything surrounded by triple quotes.
 */
const tripleQuoteStringPattern = /"""[\s\S]*"""/m;

/**
 * Matches only known DQL command names, used for keyword highlighting
 */
const dqlCommandNamePattern = new RegExp(
  `\\b(?:${sortedDqlCommands.join('|')})\\b`,
  'i',
);

/**
 * Matches any alphabetic sequence. Used for structural regex composition in
 * commandParametersPattern and commandPattern, where backtracking splits the
 * command name from the parameters. Not used for keyword highlighting.
 */
const commandNamePattern = /[A-Za-z]+/;

/**
 * Matches any string as long as it doesn't contain the character | (this is a command separator in dql)
 * or containing a string surrounded by double quotes that might contain the character | .
 *
 * Given a hypothetical dql:
 * FETCH metric = "string" | AGGREGATE X
 * This regex would match:
 * metric = "string"
 * X
 * As well as other possibilities:
 * metric = "string containing |"
 * metric = "string containing multiple |||" and secondMetric = Y
 * metric = parameter
 */
const commandParametersPattern = new RegExp(
  `(${commandNamePattern.source})(?:${stringPattern.source}|[^|])*`,
);

/**
 * A command (FILTER parameter = "string" |) is made up of three parts:
 * command name made up of one word (FETCH)
 * command parameters (parameter = "string")
 * an optional command separator (|)
 */
const commandPattern = new RegExp(
  `${commandNamePattern.source}${commandParametersPattern.source}\\|?`,
  'm',
);

const commandName = {
  commandName: {
    pattern: dqlCommandNamePattern,
    alias: 'keyword',
  },
};

/**
 * @public
 */
export const prismDqlLanguage = {
  ...comments,
  /**
   * DQL language is defined as commands separated by the optional character (|).
   * Prism first matches an entire command, then matches the command parameters and command name
   * 'inside' the command.
   * Within the command parameters, Prism matches strings, variables, and numbers.
   *
   * Take great care of adding or modifying the regex patterns as this
   * may introduce recursion issues, due to the existence of three levels of regex matching.
   */
  command: {
    pattern: commandPattern,
    greedy: true,
    inside: {
      commandParameters: {
        pattern: commandParametersPattern,
        lookbehind: true,
        inside: {
          tripleQuoteString: {
            pattern: tripleQuoteStringPattern,
            alias: 'string',
          },
          string: {
            pattern: stringPattern, // Support strings surrounded by double quotes and include possible escaped quotes within
          },
          backtickstring: {
            pattern: backtickStringPattern,
            alias: 'string',
          },
          number: new RegExp(`\\b\\d+(?:\\.\\d+)?${dqlUnitSuffixPattern}?\\b`), // Match numbers with optional unit suffix
          boolean: booleanLiteralPattern, // Match boolean literals (true/false)
          parameterName: { pattern: parameterNamePattern, alias: 'variable' },
          ...comments,
          commandName: {
            pattern: new RegExp(
              `(\\[\\s*)${dqlCommandNamePattern.source}`,
              'i',
            ),
            lookbehind: true,
            alias: 'keyword',
          },
        },
        ...commandName,
      },
      ...commandName,
    },
  },
};
